import { useFrame, useThree } from "@react-three/fiber";
import { Container, type FontFamilies } from "@react-three/uikit";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

// Gradient background shader
const gradientVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const gradientFragmentShader = `
  varying vec2 vUv;
  uniform vec3 colorTopLeft;
  uniform vec3 colorTopRight;
  uniform vec3 colorBottomLeft;
  uniform vec3 colorBottomRight;
  uniform vec3 colorCenter;
  uniform float centerBlend;

  void main() {
    // Four-corner gradient interpolation
    vec3 topColor = mix(colorTopLeft, colorTopRight, vUv.x);
    vec3 bottomColor = mix(colorBottomLeft, colorBottomRight, vUv.x);
    vec3 cornerGradient = mix(bottomColor, topColor, vUv.y);
    
    // Add radial center color blend for extra depth
    vec2 center = vUv - 0.5;
    float dist = length(center) * 2.0;
    float centerFade = 1.0 - smoothstep(0.0, 1.0, dist);
    
    vec3 finalColor = mix(cornerGradient, colorCenter, centerFade * centerBlend);
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

interface CRTUIRendererProps {
  children: ReactNode;
  threeContent?: ReactNode;
  renderTarget: THREE.WebGLRenderTarget;
  width: number;
  height: number;
  fontFamily?: string;
  fontFamilies?: FontFamilies;
}

export function CRTUIRenderer({
  children,
  threeContent,
  renderTarget,
  width,
  height,
  fontFamily,
  fontFamilies,
}: CRTUIRendererProps) {
  const { gl } = useThree();
  const sceneRef = useRef(new THREE.Scene());
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const rootGroupRef = useRef<THREE.Group>(null);
  const threeGroupRef = useRef<THREE.Group>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Create gradient background mesh
  const gradientMesh = useMemo(() => {
    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      vertexShader: gradientVertexShader,
      fragmentShader: gradientFragmentShader,
      uniforms: {
        // Cool multi-colored gradient: purple, teal, blue theme
        colorTopLeft: { value: new THREE.Color(0x4a1a7a) }, // Purple
        colorTopRight: { value: new THREE.Color(0x1a4a5a) }, // Teal
        colorBottomLeft: { value: new THREE.Color(0x1a1a3a) }, // Dark blue
        colorBottomRight: { value: new THREE.Color(0x2a3a5a) }, // Steel blue
        colorCenter: { value: new THREE.Color(0x2a2a4a) }, // Mid blue center
        centerBlend: { value: 0.4 }, // Center blend amount
      },
      depthWrite: false,
      depthTest: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    return mesh;
  }, []);

  // Create a separate orthographic camera for the gradient (normalized coordinates)
  const gradientCamera = useMemo(() => {
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    return camera;
  }, []);

  // Cleanup gradient resources
  useEffect(() => {
    return () => {
      gradientMesh.geometry.dispose();
      (gradientMesh.material as THREE.ShaderMaterial).dispose();
    };
  }, [gradientMesh]);

  useEffect(() => {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const camera = new THREE.OrthographicCamera(
      -halfWidth,
      halfWidth,
      halfHeight,
      -halfHeight,
      0.1,
      1000,
    );
    camera.position.set(0, 0, 500);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;
    setIsInitialized(true);

    return () => {
      cameraRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (cameraRef.current) {
      const halfWidth = width / 2;
      const halfHeight = height / 2;
      cameraRef.current.left = -halfWidth;
      cameraRef.current.right = halfWidth;
      cameraRef.current.top = halfHeight;
      cameraRef.current.bottom = -halfHeight;
      cameraRef.current.updateProjectionMatrix();
    }
  }, [width, height]);

  useFrame(() => {
    if (!cameraRef.current || !rootGroupRef.current) return;

    rootGroupRef.current.visible = true;
    if (threeGroupRef.current) threeGroupRef.current.visible = true;

    if (threeGroupRef.current) sceneRef.current.add(threeGroupRef.current);
    sceneRef.current.add(rootGroupRef.current);

    const currentRenderTarget = gl.getRenderTarget();
    const originalAutoClear = gl.autoClear;
    gl.setRenderTarget(renderTarget);

    // Render gradient background first (with clear)
    gl.autoClear = true;
    const gradientScene = new THREE.Scene();
    gradientScene.add(gradientMesh);
    gl.render(gradientScene, gradientCamera);
    gradientScene.remove(gradientMesh);

    // Then render the UI content on top (without clearing to preserve gradient)
    gl.autoClear = false;
    gl.render(sceneRef.current, cameraRef.current);

    // Restore original state
    gl.autoClear = originalAutoClear;
    gl.setRenderTarget(currentRenderTarget);

    if (threeGroupRef.current) {
      sceneRef.current.remove(threeGroupRef.current);
      threeGroupRef.current.visible = false;
    }
    sceneRef.current.remove(rootGroupRef.current);
    rootGroupRef.current.visible = false;
  });

  if (!isInitialized) return null;

  return (
    <>
      {threeContent && (
        <group ref={threeGroupRef} visible={true}>
          {threeContent}
        </group>
      )}
      <group ref={rootGroupRef} visible={true}>
        <Container
          width={width}
          height={height}
          flexDirection="column"
          backgroundColor="transparent"
          pixelSize={1}
          fontFamily={fontFamily}
          fontFamilies={fontFamilies}
        >
          {children}
        </Container>
      </group>
    </>
  );
}
